import { GAP_MS, REPEAT_MS, SYMBOL_MS, TONES, checkSampleRate, symbolTone } from "./codec";

// One tone plays at a time, so the level can stay well below full scale: small speakers
// distort near the top, and distortion is what turns inaudible tones into audible ones.
const TONE_GAIN = 0.35;
const RAMP_S = 0.015; // fades at both ends of a symbol, so it starts and stops without a click
const LEAD_S = 0.05; // scheduling lead, so the first symbol is not already in the past
const CLOSE_DELAY_MS = 40; // a fade-out must finish before an owned context is closed

export const AUDIO_NOT_RUNNING = "AudioNotRunningError";

const createContext = () => new (window.AudioContext || window.webkitAudioContext)();

const sameMessage = (a, b) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((symbol, index) => symbol === b[index]);

const validMessage = (symbols) =>
  Array.isArray(symbols) &&
  symbols.length > 0 &&
  symbols.every((symbol) => Number.isInteger(symbol) && symbol >= 0 && symbol < TONES.length && Number.isFinite(symbolTone(symbol)));

/** The envelope a symbol was scheduled with, evaluated at `time`: silence, attack, steady tone, release, silence. */
const envelopeAt = ({ from, to }, time) => {
  if (time <= from || time >= to) return 0;
  if (time < from + RAMP_S) return (TONE_GAIN * (time - from)) / RAMP_S;
  if (time > to - RAMP_S) return (TONE_GAIN * (to - time)) / RAMP_S;
  return TONE_GAIN;
};

/**
 * Pins a symbol's gain at the value it has at `time`, without a jump. Plain cancellation
 * drops the ramp in progress and lets the value fall back to an earlier event, which is a
 * click; `cancelAndHoldAtTime` keeps the value, and where a browser lacks it the value is
 * computed from what was scheduled and set at that same instant.
 */
const holdAt = (node, time) => {
  const param = node.gain.gain;
  if (typeof param.cancelAndHoldAtTime === "function") {
    param.cancelAndHoldAtTime(time);
    // Anchor the next ramp at cancellation even in a constant segment between events.
    // Otherwise some implementations start that ramp at the earlier attack endpoint.
    param.setValueAtTime(envelopeAt(node, time), time);
    return;
  }
  param.cancelScheduledValues(time);
  const value = envelopeAt(node, time);
  // Replace the removed ramp endpoint so the automation leading up to cancellation
  // keeps its slope. A set event alone would flatten that part of the timeline.
  if ((time > node.from && time < node.from + RAMP_S) || (time > node.to - RAMP_S && time < node.to)) {
    param.linearRampToValueAtTime(value, time);
  } else {
    param.setValueAtTime(value, time);
  }
};

/**
 * Plays messages as tones through the speaker, repeating the current message every
 * REPEAT_MS until it is replaced or stopped. A different message fades out whatever is
 * still sounding and starts at once; the same message is left to finish. `audioContext` and
 * `destination` can be supplied to route the tones somewhere else (tests, loop-back checks);
 * `onContextState` hears about the context being interrupted or running again.
 */
export function createEmitter({ audioContext, destination, repeatMs = REPEAT_MS, onContextState } = {}) {
  let ctx = audioContext || null;
  const ownsContext = !audioContext;
  let current = null;
  let timer = null;
  let playing = []; // { oscillator, gain, from, to } of the message being played
  let endsAt = 0; // audio time at which the message being played is over
  let closed = false;
  let stateHandler = null;

  const watch = (context) => {
    if (onContextState && "onstatechange" in context) {
      stateHandler = () => {
        if (ctx === context) onContextState(context.state);
      };
      context.onstatechange = stateHandler;
    }
  };

  const ensureContext = () => {
    if (closed) throw new Error("Sound sync emitter is closed");
    if (!ctx) {
      ctx = createContext();
    }
    if (!stateHandler) watch(ctx);
    return ctx;
  };

  // Fades out and stops whatever is still scheduled, so messages never overlap (overlapping
  // tones add up, clip, and come out of the speaker as audible noise).
  const silence = () => {
    if (!ctx || playing.length === 0) return;
    const now = ctx.currentTime;
    for (const node of playing) {
      if (now >= node.to) continue; // finished by itself
      holdAt(node, now);
      node.gain.gain.linearRampToValueAtTime(0, now + RAMP_S);
      try {
        node.oscillator.stop(now + RAMP_S + 0.005);
      } catch {
        // already stopped
      }
    }
    playing = [];
    endsAt = 0;
  };

  const playSymbols = (symbols) => {
    const context = ensureContext();
    silence();
    const target = destination || context.destination;
    const start = context.currentTime + LEAD_S;
    const step = (SYMBOL_MS + GAP_MS) / 1000;
    playing = symbols.map((symbol, index) => {
      const from = start + index * step;
      const to = from + SYMBOL_MS / 1000;
      const oscillator = context.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.value = symbolTone(symbol);
      const gain = context.createGain();
      gain.gain.value = 0; // silent until its attack: a gain node defaults to 1
      gain.gain.setValueAtTime(0, from);
      gain.gain.linearRampToValueAtTime(TONE_GAIN, from + RAMP_S);
      gain.gain.setValueAtTime(TONE_GAIN, to - RAMP_S);
      gain.gain.linearRampToValueAtTime(0, to);
      oscillator.connect(gain);
      gain.connect(target);
      const node = { oscillator, gain, from, to };
      oscillator.onended = () => {
        playing = playing.filter((other) => other !== node);
        try {
          oscillator.disconnect();
          gain.disconnect();
        } catch {
          // already gone
        }
      };
      oscillator.start(from);
      oscillator.stop(to + RAMP_S);
      return node;
    });
    endsAt = start + symbols.length * step;
  };

  const loop = () => {
    timer = null;
    if (!current) return;
    playSymbols(current);
    timer = setTimeout(loop, repeatMs);
  };

  return {
    /**
     * Creates the audio context and gets it running; call from a user gesture. Rejects
     * (UNSUPPORTED_SAMPLE_RATE) when the output cannot carry the tones, and
     * (AUDIO_NOT_RUNNING) when the browser will not start audio.
     */
    async start() {
      const context = ensureContext();
      checkSampleRate(context.sampleRate);
      if (context.state !== "running" && typeof context.resume === "function") await context.resume();
      if (closed) throw new Error("Sound sync startup was cancelled");
      if (context.state !== "running") {
        const error = new Error(`Audio could not start (${context.state}). Tap the button again.`);
        error.name = AUDIO_NOT_RUNNING;
        throw error;
      }
    },
    /** After an interruption: tries to get the context running again. Resolves with whether it is. */
    async resume() {
      const context = ctx;
      if (!context || closed) return false;
      if (context.state !== "running" && typeof context.resume === "function") await context.resume();
      return !closed && ctx === context && context.state === "running";
    },
    get needsRestart() { return ctx?.state === "closed"; },
    /** Plays the message now, fading out a different one that is still sounding, and keeps repeating it. */
    broadcast(symbols) {
      if (!validMessage(symbols)) throw new TypeError("Sound sync: that is not a message");
      const context = ensureContext();
      checkSampleRate(context.sampleRate);
      if (sameMessage(symbols, current) && playing.length > 0 && context.currentTime < endsAt) {
        current = symbols; // the same message is still sounding: let it finish
        return;
      }
      current = symbols;
      clearTimeout(timer);
      loop();
    },
    stop() {
      current = null;
      clearTimeout(timer);
      timer = null;
      silence();
    },
    close() {
      if (closed) return;
      this.stop();
      const context = ctx;
      closed = true;
      ctx = null;
      if (context && context.onstatechange === stateHandler) context.onstatechange = null;
      if (context && ownsContext && typeof context.close === "function") {
        const fadeEnd = context.currentTime + RAMP_S + 0.005;
        // A wall-clock delay alone does not establish that the audio fade has rendered.
        const finish = () => {
          if (context.state === "running" && context.currentTime < fadeEnd) {
            setTimeout(finish, CLOSE_DELAY_MS);
            return;
          }
          Promise.resolve(context.close()).catch(() => {});
        };
        setTimeout(finish, CLOSE_DELAY_MS);
      }
    },
    get message() {
      return current;
    },
  };
}
